import React, { useContext } from "react";
import _isEmpty from "lodash/isEmpty";
import validator from "validator";
import { useEffectOnce, useInput } from "@lib-shared/hooks";
import { MenuArea } from "core/constants";
import { AppContext } from "core/context";
import { logApiError } from "core/lib";
import { autoTextBoxHeightMax } from "core/lib/auto-textbox-height";
import User, { getUser } from "core/models/user";
import CaseManagementApi from "core/services/case-management.service";
import { CaseCategoryOptions } from "../case-management/constants";
import { ModalAlert } from "components/common-ui";
import SelectX from "components/ui/form/select";
import { FormField, ValidationMessage } from "./form-field";

const MahForm = () => {
  const appContext = useContext(AppContext);

  appContext.updateArea(MenuArea.queries);

  //Was occassionally getting an error so added this.
  if (!appContext.user) {
    getUser();
  }

  const user = appContext.user as User;

  const options = (id: string, initialValue: string | undefined, placeholder: string, validationMessage: string | null = null) => {
    return {
      id,
      initialValue,
      placeholder,
      validationMessage: validationMessage ?? `${placeholder} Required`
    }
  };

  const inputOptions = {
    nameSurname: options("fullname", user?.fullName, "Name & Surname", "Please enter your name and surname"),
    businessName: options("businessName", user?.businessName, "Business Name", "Please enter the business name"),
    emailAddress: options("email", user?.email, "Email Address", "Please enter an email address"),
    accountNumber: options("accountNumber", (user?.company?.customerNumber ?? "").toString(), "Account Number", "Please enter the account number"),
    subject: options("subject", undefined, "Please provide a subject for your message"),
    description: options("description", undefined, "Description", "Please include a brief description of your query"),
    category: options("category", "Billing", "Please select a category from the options provided"),
  }

  const inputValidation = (value: string) => !_isEmpty(value);

  const emailValidation = (value: string) => {
    const result = inputValidation(value) && validator.isEmail(value);
    return result;
  }

  const nameSurnameInput = useInput(inputValidation, inputOptions.nameSurname);
  const businessNameInput = useInput(inputValidation, inputOptions.businessName);
  const descriptionInput = useInput(inputValidation, inputOptions.description);
  const emailAddressInput = useInput(emailValidation, inputOptions.emailAddress);
  const accountNumberInput = useInput(inputValidation, inputOptions.accountNumber);
  const subjectInput = useInput(inputValidation, inputOptions.subject);
  const categoryInput = useInput(inputValidation, inputOptions.category);

  const inputs = [
    nameSurnameInput,
    businessNameInput,
    descriptionInput,
    emailAddressInput,
    accountNumberInput,
    subjectInput,
    categoryInput
  ];

  const areInputsAllValid = () => !inputs.some(x => !x.isValid);

  const formIsValid = areInputsAllValid();

  let isSubmitDisabled = true;

  !formIsValid ? isSubmitDisabled = true : isSubmitDisabled = false;

  const resetAll = () => inputs.forEach(x => x.reset());

  useEffectOnce(() => {
    appContext.updateHeader("Queries");
  });

  const modalOptions = { buttonText: "Done" };

  const showErrorModal = () => ModalAlert.error("Sorry something went wrong while submitting your query.", "Error", modalOptions);

  const submitHandler = async (event: any) => {
    event.preventDefault();

    const formData = {
      nameSurname: nameSurnameInput.value,
      businessName: businessNameInput.value,
      description: descriptionInput.value,
      emailAddress: emailAddressInput.value,
      accountNumber: accountNumberInput.value,
      subject: subjectInput.value,
      category: categoryInput.value
    }

    let dialog: HTMLDialogElement | null = null;

    try {
      dialog = ModalAlert.loading("Submitting Query");

      const response = await new CaseManagementApi().sendMail(formData, user.countryCode);

      if (dialog != null) {
        ModalAlert.close(dialog);
        dialog = null;
      }

      if (response?.data === true) {
        ModalAlert.success("That was easy!", "Thank you, we have received your query submission. Please check your emails for confirmation.", modalOptions);
        event.target.reset();
        return;
      }

      showErrorModal();
    } catch (error) {
      if (dialog != null) {
        ModalAlert.close(dialog);
        dialog = null;
      }

      showErrorModal();
      logApiError(error);
    }
  }

  return (
    <div className="floaty-form modal-form mah-cm">
      <form onSubmit={submitHandler} onReset={resetAll} className="mah-cm-form new-query">
        <h2>
          Log a new query
        </h2>

        <div className="row two-cols">
          <FormField input={nameSurnameInput} />

          <FormField input={businessNameInput} />
        </div>

        <div className="row two-cols">
          <FormField input={emailAddressInput} />

          <FormField input={accountNumberInput} />
        </div>

        <div className="row two-cols">
          <FormField input={subjectInput} />

          <div className="col react-select">
            <SelectX options={CaseCategoryOptions}
              name="category"
              label="Category"
              isClearable={false}
              useFloaty={true}
              onHandleChange={(o) => { categoryInput.setValue(o?.value) }} />
          </div>
          <ValidationMessage input={categoryInput} />
        </div>

        <div className="row col rounded-scrollbar">
          <textarea
            id="description"
            onInput={(e) => autoTextBoxHeightMax(e)}
            rows={1}
            placeholder="Description"
            onChange={descriptionInput.valueChangeHandler}
            onBlur={descriptionInput.inputBlurHandler} />

          <label htmlFor="description">Description</label>

          <ValidationMessage input={descriptionInput} />
        </div>

        <div className="row modal-buttons-row">
          <button className="btn btn-outline-black" type="reset">
            Clear
          </button>
          <button className="btn btn-black" type="submit" disabled={isSubmitDisabled}>
            Submit
          </button>
        </div>
      </form>
    </div>
  );
}

export default MahForm;
